import type {Placement} from '@popperjs/core';
import React, {FC, RefObject} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {
  ReplyFactModel,
  ReplyFactReferenceModel,
} from '../../../../../libs/shared/core/src/models/api/factModel';
import {fontSizes} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {basicMarkdownStyle} from '../../../../../libs/shared/ui/src/styles/markdownStyles';
import {DomMarkdownRenderer} from '../../../common/formatters/markdown/domMarkdownRenderer';
import {markdownDomComponents} from '../../../common/formatters/markdown/markdownDomComponents';
import {ArticleLinkButton} from './articleLinkButton';
import {TriggerPreview} from './triggerPreview';

/*
 * Props.
 */

interface AiReplyPreviewProps {
  anchor: RefObject<HTMLElement>;
  popperPlacement: Placement;
  aiReply: ReplyFactModel;
  onSourceClick: (reference: ReplyFactReferenceModel) => void;
  shouldHideSources?: boolean;
}

/*
 * Styles.
 */

const StyledOutlineDiv = styled.div`
  line-height: 20px;
  color: ${(p) => p.theme.text};

  &,
  * {
    cursor: text;
    -webkit-user-select: text;
    user-select: text;
  }

  ${basicMarkdownStyle()}

  ul {
    margin: 0;
    padding-inline-start: 12px;
  }

  ol {
    margin: 0;
    padding-inline-start: 16px;
  }

  code {
    white-space: pre-wrap;
  }
`;

const StyledSourcesDiv = styled.div`
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.verySmall};
  margin-bottom: 8px;
`;

const StyledReferencesDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

/*
 * Component.
 */

export const AiReplyPreview: FC<AiReplyPreviewProps> = (props) => {
  const {anchor, popperPlacement, aiReply, onSourceClick, shouldHideSources} = props;

  return (
    <TriggerPreview
      anchor={anchor}
      popperPlacement={popperPlacement}
      header={<Trans id="suggested-reply-title">Suggested reply outline</Trans>}
      content={
        <StyledOutlineDiv>
          <DomMarkdownRenderer
            debugIdentifier="ai-reply-preview"
            text={aiReply.outline}
            components={markdownDomComponents}
          />
        </StyledOutlineDiv>
      }
      footer={
        !shouldHideSources &&
        aiReply.references.length > 0 && (
          <>
            <StyledSourcesDiv>
              <Trans id="suggested-reply-sources">Sources</Trans>
            </StyledSourcesDiv>
            <StyledReferencesDiv>
              {aiReply.references.map((reference) => (
                <ArticleLinkButton
                  key={reference.href}
                  name={reference.name}
                  onClick={() => onSourceClick(reference)}
                />
              ))}
            </StyledReferencesDiv>
          </>
        )
      }
    />
  );
};
